import { zodMoment } from '@qapline/helpers';
import { z } from 'zod';

import { commissionSchema } from '../../commission/schemas/commission';
import { commissionDischargeSchema } from '../../commission/schemas/commissionDischarge';
import { commissionLoadingSchema } from '../../commission/schemas/commissionLoading';
import { currencyEnum } from '../../common/schemas/currency';
import { customerSchema } from '../../customer/schemas/customer';

export const invoiceCommissionSchema = commissionSchema.extend({
    loadings: z.array(commissionLoadingSchema),
    discharges: z.array(commissionDischargeSchema),
});
export type TInvoiceCommission = z.infer<typeof invoiceCommissionSchema>;

export const invoicePaidStateChangedBySchema = z.object({
    user_id: z.number(),
    name: z.string(),
    surname: z.string(),
    email: z.string(),
});
export type TInvoicePaidStateChangedBy = z.infer<typeof invoicePaidStateChangedBySchema>;

export const invoiceLanguageSchema = z.object({
    language_id: z.number(),
    languageCode: z.string(),
});
export type TInvoiceLanguage = z.infer<typeof invoiceLanguageSchema>;

export const invoicePaymentStatusEnum = z.enum(['PAID', 'UNPAID', 'OVERPAID', 'UNDERPAID']);
export type TInvoicePaymentStatus = z.infer<typeof invoicePaymentStatusEnum>;

export const invoiceStatusEnum = z.enum([
    'CREATED',
    'WAITING_FOR_PAYMENT',
    'UPCOMING_PAYMENT',
    'OVERDUE',
    'MONTH_OVERDUE',
    'PAID',
    'CANCELED',
]);
export type TInvoiceStatus = z.infer<typeof invoiceStatusEnum>;

export const customerInvoiceStatusEnum = z.enum([
    'WAITING_FOR_PAYMENT',
    'UPCOMING_PAYMENT',
    'OVERDUE',
    'PAID',
    'CANCELED',
]);
export type TCustomerInvoiceStatus = z.infer<typeof customerInvoiceStatusEnum>;

export const invoiceSchema = z.object({
    invoice_id: z.number(),
    number: z.string(),
    currency: currencyEnum,
    status: invoiceStatusEnum,

    totalPriceNet: z.number({ coerce: true }).nullable(),
    totalPriceGross: z.number({ coerce: true }).nullable(),

    variableSymbol: z.string().nullable(),
    constantSymbol: z.string().nullable(),

    dateOrder: zodMoment().nullable(),
    dateIssue: zodMoment().nullable(),
    datePoint: zodMoment().nullable(),
    dateDue: zodMoment().nullable(),

    dateExported: zodMoment().nullable(),

    paid: z.boolean(),
    paidAmount: z.number({ coerce: true }).nullable(),
    paymentStatus: invoicePaymentStatusEnum,
    paymentReminderCount: z.number(),
    paymentConfirmationDate: zodMoment().nullable(),
    paidStateChangedBy_id: z.number().nullable(),

    language_id: z.number(),
    customerVersion_id: z.number(),

    invoiceAttachment_id: z.number().nullable(),

    deleted: z.boolean(),
});
export type TInvoice = z.infer<typeof invoiceSchema>;

export const extendedInvoiceSchema = invoiceSchema.extend({
    customer: customerSchema.nullable(),
    commissions: invoiceCommissionSchema.array().nullable(),
    paidStateChangedBy: invoicePaidStateChangedBySchema.nullable(),
    language: invoiceLanguageSchema,
});
export type TExtendedInvoice = z.infer<typeof extendedInvoiceSchema>;
